import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Train, Truck, Ship, MoveRight } from "lucide-react";
import { useLang } from "@/i18n";
import Reveal from "./Reveal";

export default function Services() {
  const { t } = useLang();
  const [active, setActive] = useState(0);

  const services = [
    { icon: Train, t: t("svc_rail_t"), d: t("svc_rail_d"), img: "/brand/rail-container.jpg", n: "01" },
    { icon: Truck, t: t("svc_road_t"), d: t("svc_road_d"), img: "/brand/road-daf.jpg", n: "02" },
    { icon: Ship, t: t("svc_sea_t"), d: t("svc_sea_d"), img: "https://images.unsplash.com/photo-1598408745613-178751e2ccde", n: "03" },
  ];

  return (
    <section id="services" data-testid="services" className="relative bg-[#ffffff] py-24 md:py-32">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#d81f2a]">
            {t("services_overline")}
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase leading-[0.95] tracking-tight text-[#0f1115]">
            {t("services_title")}
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          {/* Accordion list */}
          <div className="border-t border-black/10">
            {services.map((s, i) => {
              const Icon = s.icon;
              const isActive = active === i;
              return (
                <div key={i} className="border-b border-black/10">
                  <button
                    data-testid={`service-tab-${i}`}
                    onClick={() => setActive(i)}
                    className="w-full flex items-center gap-6 py-7 text-left group"
                  >
                    <span className={`font-mono text-sm ${isActive ? "text-[#d81f2a]" : "text-[#5b6472]"}`}>
                      {s.n}
                    </span>
                    <Icon
                      className={`w-6 h-6 transition-colors ${isActive ? "text-[#d81f2a]" : "text-[#5b6472] group-hover:text-[#0f1115]"}`}
                    />
                    <span
                      className={`font-display text-2xl md:text-3xl font-bold uppercase tracking-tight transition-colors ${
                        isActive ? "text-[#0f1115]" : "text-[#5b6472] group-hover:text-[#0f1115]"
                      }`}
                    >
                      {s.t}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-8 pl-16 pr-4 font-body text-base text-[#5b6472] leading-relaxed">
                          {s.d}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Image */}
          <div className="relative min-h-[360px] lg:min-h-full overflow-hidden border border-black/10">
            <AnimatePresence mode="wait">
              <motion.img
                key={active}
                src={services[active].img}
                alt={services[active].t}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-[#ffffff]/30" />
          </div>
        </div>

        {/* Transport routes */}
        <div className="mt-16 border-t border-black/10 pt-10" data-testid="routes">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight text-[#0f1115]">
              {t("routes_title")}
            </h3>
            <p className="font-body text-sm text-[#5b6472] max-w-md">{t("routes_sub")}</p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {[
              ["ÇİN", "ORTA ASYA"],
              ["BAE / KÖRFEZ", "ORTA ASYA ÜLKELERİ"],
              ["TÜRKİYE", "ORTA ASYA"],
            ].map((r, i) => (
              <div key={i} className="bg-[#ffffff] p-6 flex items-center gap-4 group hover:bg-[#ffffff] transition-colors" data-testid={`route-${i}`}>
                <span className="font-display text-lg font-bold uppercase tracking-tight text-[#0f1115]">{r[0]}</span>
                <MoveRight className="w-5 h-5 text-[#d81f2a] shrink-0" />
                <span className="font-display text-lg font-bold uppercase tracking-tight text-[#5b6472] group-hover:text-[#0f1115] transition-colors">{r[1]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
